// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect } from 'react';

import { isNil } from 'helpers/isNotNil';
import { type CartBendId, useBendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import type { ValidatedFormData } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorForm';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import {
  type BendCreatorStepIndex,
  bendCreatorStepIndexes,
} from 'modules/Shop/EvolveFabrication/BendCreator/Nav/bendCreatorSteps';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';

/** How the bend being edited stands against validation. */
export type CopyEditStatus = 'valid' | 'validating' | 'invalid';

/**
 * Editing a bend from the list in the nav panel.
 *
 * While editing, the panel's form holds the listed bend, and the bend being built is kept aside and restored when
 * editing ends. Every time the edited values pass validation they are written to the list, so the listed bend is
 * always valid: ending an edit any way other than Cancel keeps its last valid values.
 */
export const useCopyEditing = () => {
  const form = useBendCreatorFormContext();
  const { lastValidation, isValidating, restoreLastValidation } = useBendCreatorValidationContext();
  const { bends, restoreBend, updateBend, expandBend } = useBendCart();
  const {
    editSession,
    setEditSession,
    setFinishedSteps,
    finishedSteps,
    isAutoNamed,
    setIsAutoNamed,
    openStep,
    openRoot,
  } = useBendBuilder();

  const editingBendId = editSession?.bendSnapshot.cartBendId ?? null;
  const status: CopyEditStatus | null = isNil(editSession)
    ? null
    : isValidating
      ? 'validating'
      : lastValidation?.isSuccess === true && form.isValid()
        ? 'valid'
        : 'invalid';

  const endEditing = useCallback(
    (restoreSnapshot: boolean) => {
      if (isNil(editSession)) return;
      if (restoreSnapshot) restoreBend(editSession.bendSnapshot);
      const { builderSnapshot } = editSession;
      form.setValues(builderSnapshot.values);
      form.clearErrors();
      restoreLastValidation(builderSnapshot.lastValidation);
      setFinishedSteps(builderSnapshot.finishedSteps);
      setIsAutoNamed(builderSnapshot.isAutoNamed);
      setEditSession(null);
      openRoot();
    },
    [editSession, restoreBend, form, restoreLastValidation, setFinishedSteps, setIsAutoNamed, setEditSession, openRoot],
  );

  /** Opens a step in the panel for a listed bend. Switching from another listed bend keeps that one's valid values. */
  const startEditing = useCallback(
    (cartBendId: CartBendId, step: BendCreatorStepIndex) => {
      if (editingBendId === cartBendId) {
        openStep(step);
        return;
      }
      const bend = bends.find((candidate) => candidate.cartBendId === cartBendId);
      if (isNil(bend)) return;
      // When switching between listed bends, the bend being built was already set aside by the first one.
      const builderSnapshot = editSession?.builderSnapshot ?? {
        values: form.getValues(),
        lastValidation,
        finishedSteps,
        isAutoNamed,
      };
      form.setValues(bend.values);
      form.clearErrors();
      restoreLastValidation({ ...bend.validation, isSuccess: true });
      // A listed bend is complete, so every step shows as done.
      setFinishedSteps(new Set(bendCreatorStepIndexes));
      setIsAutoNamed(bend.isAutoNamed);
      setEditSession({ bendSnapshot: bend, builderSnapshot });
      expandBend(cartBendId);
      openStep(step);
    },
    [
      editingBendId,
      bends,
      editSession,
      form,
      lastValidation,
      finishedSteps,
      isAutoNamed,
      restoreLastValidation,
      setFinishedSteps,
      setIsAutoNamed,
      setEditSession,
      expandBend,
      openStep,
    ],
  );

  /** Changes the name or quantity of a listed bend from its row, keeping the panel's form in step if it's being edited. */
  const changeListedBend = useCallback(
    (cartBendId: CartBendId, changes: Partial<Pick<ValidatedFormData, 'partName' | 'quantity'>>) => {
      const bend = bends.find((candidate) => candidate.cartBendId === cartBendId);
      if (isNil(bend)) return;
      const renamed = 'partName' in changes;
      updateBend(cartBendId, {
        values: { ...bend.values, ...changes },
        ...(renamed ? { isAutoNamed: false } : {}),
      });
      if (editingBendId !== cartBendId) return;
      if (renamed && !isNil(changes.partName)) {
        form.setFieldValue('partName', changes.partName);
        setIsAutoNamed(false);
      }
      if (!isNil(changes.quantity)) form.setFieldValue('quantity', changes.quantity);
    },
    [bends, updateBend, editingBendId, form, setIsAutoNamed],
  );

  return {
    editingBendId,
    status,
    startEditing,
    /** Keeps the edits and returns to the list of steps. */
    finishEditing: useCallback(() => endEditing(false), [endEditing]),
    /** Puts the listed bend back as it was before editing began, then returns to the list of steps. */
    cancelEditing: useCallback(() => endEditing(true), [endEditing]),
    changeListedBend,
  };
};

/**
 * Writes the edited bend to the list each time its values pass validation. Validation finishes asynchronously, so
 * this reacts to its result rather than to a user action. Call once, near the root of the Bend Creator.
 */
export const useCopyWriteThrough = () => {
  const { values } = useBendCreatorFormContext();
  const { lastValidation } = useBendCreatorValidationContext();
  const { updateBend } = useBendCart();
  const { isAutoNamed } = useBendBuilder();
  const { editingBendId, status } = useCopyEditing();

  useEffect(() => {
    if (isNil(editingBendId) || status !== 'valid' || lastValidation?.isSuccess !== true) return;
    updateBend(editingBendId, {
      // Validation has passed, so every required field is non-null.
      values: values as ValidatedFormData,
      validation: { request: lastValidation.request, response: lastValidation.response },
      isAutoNamed,
    });
  }, [editingBendId, status, lastValidation, values, isAutoNamed, updateBend]);
};
